import type { CoreTenant, CoreTenantSite } from '../types';

const CATALOG_KEY = 'wandawazi_known_tenants';

function siteKey(site: CoreTenantSite): string {
  return site.tenantSiteId || site.id;
}

function mergeSites(current: CoreTenantSite[], incoming: CoreTenantSite[]): CoreTenantSite[] {
  const sites = new Map<string, CoreTenantSite>();
  for (const site of [...current, ...incoming]) {
    const key = siteKey(site);
    if (!key) continue;
    sites.set(key, { ...sites.get(key), ...site });
  }
  return [...sites.values()];
}

export function mergeTenantLists(existing: CoreTenant[], incoming: CoreTenant[]): CoreTenant[] {
  const tenants = new Map<string, CoreTenant>();
  for (const tenant of existing) {
    if (tenant?.id) tenants.set(tenant.id, tenant);
  }
  for (const tenant of incoming) {
    if (!tenant?.id) continue;
    const previous = tenants.get(tenant.id);
    if (!previous) {
      tenants.set(tenant.id, { ...tenant, sites: tenant.sites ?? [] });
      continue;
    }
    tenants.set(tenant.id, {
      ...previous,
      ...tenant,
      name: tenant.name || previous.name,
      sites: mergeSites(previous.sites ?? [], tenant.sites ?? []),
    });
  }
  return [...tenants.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function readCatalog(): Record<string, CoreTenant[]> {
  const raw = localStorage.getItem(CATALOG_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, CoreTenant[]>;
  } catch {
    return {};
  }
}

export function knownTenants(userId: string | null): CoreTenant[] {
  if (!userId) return [];
  const list = readCatalog()[userId];
  return Array.isArray(list) ? list : [];
}

/** Keeps organisations seen on earlier tokens, even if the current role cannot see them. */
export function rememberTenants(userId: string | null, incoming: CoreTenant[]): CoreTenant[] {
  if (!userId) return incoming;
  const merged = mergeTenantLists(knownTenants(userId), incoming);
  const catalog = readCatalog();
  catalog[userId] = merged;
  localStorage.setItem(CATALOG_KEY, JSON.stringify(catalog));
  return merged;
}
